import {
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  shallowRef,
  watch,
  type Ref,
} from 'vue'

export function useElementWidth(elementRef: Ref<HTMLElement | null>, fallbackWidth = 0) {
  const width = shallowRef(fallbackWidth)
  let resizeObserver: ResizeObserver | null = null
  let isObserverActive = true

  function stopObserving() {
    resizeObserver?.disconnect()
    resizeObserver = null
  }

  function updateWidth() {
    const nextWidth = elementRef.value?.clientWidth ?? fallbackWidth

    if (nextWidth > 0) {
      width.value = nextWidth
    }
  }

  function startObserving() {
    stopObserving()

    if (!isObserverActive) {
      return
    }

    updateWidth()

    if (!elementRef.value || typeof ResizeObserver === 'undefined') {
      return
    }

    resizeObserver = new ResizeObserver(([entry]) => {
      const nextWidth = entry?.contentRect.width ?? elementRef.value?.clientWidth ?? fallbackWidth

      if (nextWidth > 0) {
        width.value = nextWidth
      }
    })
    resizeObserver.observe(elementRef.value)
  }

  onMounted(startObserving)
  onActivated(() => {
    isObserverActive = true
    startObserving()
  })
  onDeactivated(() => {
    isObserverActive = false
    stopObserving()
  })
  onBeforeUnmount(() => {
    isObserverActive = false
    stopObserving()
  })

  watch(
    elementRef,
    () => {
      if (isObserverActive) {
        void nextTick(startObserving)
      }
    },
    { flush: 'post' },
  )

  return {
    width,
  }
}
